import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface FxState {
  xcdToUsd: number;
  /** Rates from open.er-api: units of target currency per 1 XCD */
  ratesFromXcd: Record<string, number>;
  lastFetchedAt: string | null;
  /** Manually set XCD→USD rate — overrides API USD until cleared */
  adminOverride: number | null;
  setAdminOverride: (rate: number | null) => void;
  fetchRates: () => Promise<void>;
}

const DEFAULT_RATES: Record<string, number> = {
  USD: 0.37,
  EUR: 0.34,
  GBP: 0.29,
  CAD: 0.5,
  BBD: 0.74,
  TTD: 2.5,
  JMD: 58,
};

export const useFxStore = create<FxState>()(
  persist(
    (set, get) => ({
      xcdToUsd: DEFAULT_RATES.USD,
      ratesFromXcd: { ...DEFAULT_RATES },
      lastFetchedAt: null,
      adminOverride: null,

      setAdminOverride: (rate) => {
        set((state) => ({
          adminOverride: rate,
          ...(rate != null
            ? {
                xcdToUsd: rate,
                ratesFromXcd: { ...state.ratesFromXcd, USD: rate },
              }
            : {}),
        }));
      },

      fetchRates: async () => {
        if (get().adminOverride != null) return;
        try {
          const res = await fetch('https://open.er-api.com/v6/latest/XCD');
          if (!res.ok) return;
          const json = (await res.json()) as { rates?: Record<string, number> };
          const rates = json?.rates;
          if (!rates || typeof rates.USD !== 'number' || rates.USD <= 0) return;
          set({
            ratesFromXcd: { ...DEFAULT_RATES, ...rates },
            xcdToUsd: rates.USD,
            lastFetchedAt: new Date().toISOString(),
          });
        } catch {
          // network unavailable — keep previous rates
        }
      },
    }),
    { name: 'shopgnd-fx' },
  ),
);

/** Non-hook accessor so plain functions in currency.ts can read rates */
export function getXcdToUsd(): number {
  return useFxStore.getState().xcdToUsd;
}

/** Target currency units per 1 XCD (same units as open.er-api base XCD). */
export function getRateXcdTo(target: string): number {
  const { ratesFromXcd, xcdToUsd, adminOverride } = useFxStore.getState();
  if (target === 'XCD') return 1;
  if (target === 'USD' && adminOverride != null) return adminOverride;
  const rate = ratesFromXcd[target];
  if (typeof rate === 'number' && rate > 0) return rate;
  if (target === 'USD') return xcdToUsd;
  return DEFAULT_RATES[target] ?? 1;
}

/** Call once at app startup; skips if fetched within last 6 hours */
export function maybeRefreshRates() {
  const { lastFetchedAt, adminOverride, fetchRates } = useFxStore.getState();
  if (adminOverride != null) return;
  if (lastFetchedAt) {
    const age = Date.now() - new Date(lastFetchedAt).getTime();
    if (age < 6 * 60 * 60 * 1000) return;
  }
  void fetchRates();
}
